import type { SxProps, Theme } from '@mui/material';
import { COLORS, PLAYER_SIZES, type PlayerSize } from './utils/constants';

/**
 * Get container styles based on size
 */
export const getContainerStyles = (size: PlayerSize): SxProps<Theme> => {
  // const sizeConfig = PLAYER_SIZES[size];

  return {
    '&:hover': {
      boxShadow: '0 4px 15px rgba(88, 101, 242, 0.4)',
    },
    alignItems: 'center',
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
    borderRadius: '9999px', // Always pill-shaped regardless of size
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
    boxSizing: 'border-box',
    display: 'flex',
    gap: size === 'small' ? 1 : 1.5,
    maxWidth: '100%', // Prevent overflow
    minWidth: size === 'small' ? 200 : size === 'medium' ? 280 : 360,
    padding: size === 'small' ? '6px 12px 6px 6px' : '8px 16px 8px 8px',
    transition: 'all 0.2s ease',
    width: size === 'small' ? 360 : size === 'medium' ? 500 : 600,
  };
};

/**
 * Play/Pause button styles
 */
export const getPlayButtonStyles = (size: PlayerSize): SxProps<Theme> => {
  const sizeConfig = PLAYER_SIZES[size];

  return {
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      color: 'rgba(88, 101, 242, 0.5)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '&:hover': {
      backgroundColor: COLORS.white,
      transform: 'scale(1.05)',
    },
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: COLORS.primary,
    height: sizeConfig.buttonSize,
    minWidth: sizeConfig.buttonSize,
    transition: 'all 0.15s ease',
    width: sizeConfig.buttonSize,
  };
};

/**
 * Speed control button styles
 */
export const getSpeedButtonStyles = (): SxProps<Theme> => ({
  '&:hover': {
    backgroundColor: COLORS.speedButtonHover,
  },
  backgroundColor: COLORS.speedButtonBg,
  borderRadius: '4px',
  cursor: 'pointer',
  padding: '2px 8px',
  transition: 'background-color 0.15s ease',
});

/**
 * Time display styles
 */
export const getTimeDisplayStyles = (size: PlayerSize): SxProps<Theme> => {
  const sizeConfig = PLAYER_SIZES[size];

  return {
    color: 'rgba(255, 255, 255, 0.9)',
    fontFamily: 'monospace',
    fontSize: sizeConfig.fontSize,
    fontWeight: 500,
    minWidth: 40,
    textAlign: 'right',
  };
};

/**
 * Waveform bar styles
 */
export const getWaveformBarStyles = (
  height: number,
  isPlayed: boolean,
  barWidth: number,
): SxProps<Theme> => ({
  backgroundColor: isPlayed ? COLORS.waveformPlayed : COLORS.waveformUnplayed,
  borderRadius: '2px',
  height: `${height * 100}%`,
  minHeight: 4,
  transition: 'background-color 0.1s ease, height 0.1s ease',
  width: barWidth,
});

/**
 * Waveform container styles
 */
export const getWaveformContainerStyles = (clickable: boolean, barGap: number): SxProps<Theme> => ({
  alignItems: 'center',
  cursor: clickable ? 'pointer' : 'default',
  display: 'flex',
  flex: 1,
  gap: `${barGap}px`,
  height: 32,
  justifyContent: 'space-between',
  padding: '4px 0',
  position: 'relative',
});

/**
 * Loading spinner overlay styles
 */
export const loadingOverlayStyles: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: 'rgba(88, 101, 242, 0.3)',
  borderRadius: 'inherit',
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
};

/**
 * Speed control text styles
 */
export const speedTextStyles: SxProps<Theme> = {
  color: 'white',
  fontSize: '0.75rem',
  fontWeight: 600,
  userSelect: 'none',
};

/**
 * Volume button styles
 */
export const volumeButtonStyles: SxProps<Theme> = {
  '&:hover': {
    backgroundColor: COLORS.buttonHover,
    color: COLORS.white,
  },
  color: 'rgba(255, 255, 255, 0.9)',
  padding: 0.5,
  transition: 'all 0.15s ease',
};

/**
 * Volume slider container styles
 */
export const volumeSliderContainerStyles: SxProps<Theme> = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: 120,
  padding: 2,
};

/**
 * Volume slider styles
 */
export const volumeSliderStyles: SxProps<Theme> = {
  '& .MuiSlider-rail': {
    opacity: 0.3,
    width: 4,
  },
  '& .MuiSlider-thumb': {
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(88, 101, 242, 0.16)',
    },
    height: 12,
    width: 12,
  },
  '& .MuiSlider-track': {
    width: 4,
  },
  color: COLORS.primary,
  height: '100%',
};
